import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import useSort from "@/lib/useSort";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Plus, Trash2, Send, Truck, CheckCircle2, XCircle, Pencil, FileText, Search, Sparkles, Download, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useHasPerm } from "@/components/PermissionGate";

const STATE_LABELS = {
  bozza: "Bozza",
  da_inviare: "Da inviare",
  in_consegna: "In consegna",
  consegnato: "Consegnato",
  annullato: "Annullato",
};

const STATE_COLORS = {
  bozza: { bg: "rgba(138,115,88,0.12)", color: "#8A7358" },
  da_inviare: { bg: "rgba(217,160,91,0.18)", color: "#985741" },
  in_consegna: { bg: "rgba(30,107,138,0.15)", color: "#1E6B8A" },
  consegnato: { bg: "rgba(123,139,111,0.18)", color: "#5d6e51" },
  annullato: { bg: "rgba(139,58,54,0.12)", color: "#8B3A36" },
};

const EMPTY_LINE = { prodotto_id: "", quantita: "", ordine_unita: "pz", prezzo_unitario: "", iva_percent: 22 };
const EMPTY_ORDER = {
  fornitore_id: "",
  deposito_id: "",
  stato: "bozza",
  note: "",
  data_consegna_prevista: "",
  righe: [{ ...EMPTY_LINE }],
};

function StateBadge({ stato }) {
  const c = STATE_COLORS[stato] || STATE_COLORS.bozza;
  return (
    <span
      className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full"
      style={{ background: c.bg, color: c.color }}
    >
      {STATE_LABELS[stato] || stato}
    </span>
  );
}

export default function Ordini() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const hasPerm = useHasPerm();
  const canCreate = hasPerm("ordini:create");
  const canEdit = hasPerm("ordini:edit");
  const canDelete = hasPerm("ordini:delete");
  const canSend = hasPerm("ordini:send");
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ORDER);
  const [deleteId, setDeleteId] = useState(null);
  const [statusChange, setStatusChange] = useState(null); // {id, stato}
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedStati = ["tutti", "bozza", "da_inviare", "in_consegna", "consegnato", "annullato"];
  const initialStato = allowedStati.includes(searchParams.get("stato")) ? searchParams.get("stato") : "tutti";
  const [filter, setFilter] = useState(initialStato);
  const [search, setSearch] = useState("");

  // Keep filter dropdown in sync when URL changes (e.g. clicking Dashboard cards while on Ordini)
  useEffect(() => {
    const q = searchParams.get("stato");
    if (q && allowedStati.includes(q) && q !== filter) setFilter(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Reflect user-driven filter changes back to the URL
  const changeFilter = (v) => {
    setFilter(v);
    const next = new URLSearchParams(searchParams);
    if (v === "tutti") next.delete("stato");
    else next.set("stato", v);
    setSearchParams(next, { replace: true });
  };
  const [autoGenOpen, setAutoGenOpen] = useState(false);
  const [emailDialog, setEmailDialog] = useState(null); // order
  const [emailRecipient, setEmailRecipient] = useState("");
  const [lineSearch, setLineSearch] = useState(""); // filters product rows in the order form

  const load = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.get("/orders"),
        api.get("/suppliers"),
        api.get("/deposits"),
        api.get("/products"),
        api.get("/units"),
      ]);
      const [o, s, d, p, u] = results;
      if (o.status === "rejected") {
        toast.error("Errore caricamento ordini");
      } else {
        setOrders(o.value.data);
      }
      setSuppliers(s.status === "fulfilled" ? s.value.data : []);
      setDeposits(d.status === "fulfilled" ? d.value.data : []);
      setProducts(p.status === "fulfilled" ? p.value.data : []);
      setUnits(u.status === "fulfilled" ? u.value.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Sorted alphabetically + filter products by selected supplier
  const suppliersSorted = useMemo(
    () => [...suppliers].sort((a, b) => a.ragione_sociale.localeCompare(b.ragione_sociale, "it")),
    [suppliers]
  );
  const depositsSorted = useMemo(
    () => [...deposits].sort((a, b) => a.nome.localeCompare(b.nome, "it")),
    [deposits]
  );
  // Products available in the new-order line product picker — filtered by supplier (any deposit)
  // Deduplicate by (nome_articolo + fornitore_id) so the user picks ONE entry per product
  // Aggregate stock across all deposits and remember last purchase price
  const productsForOrder = useMemo(() => {
    let pool = products;
    if (form?.fornitore_id) pool = pool.filter((p) => p.fornitore_id === form.fornitore_id);
    const groups = new Map();
    for (const p of pool) {
      const k = `${(p.nome_articolo || "").trim().toLowerCase()}::${p.fornitore_id || ""}`;
      if (!groups.has(k)) groups.set(k, { primary: p, totalQta: 0 });
      groups.get(k).totalQta += Number(p.quantita) || 0;
    }
    return Array.from(groups.values())
      .map(({ primary, totalQta }) => ({ ...primary, _stock: totalQta }))
      .sort((a, b) => a.nome_articolo.localeCompare(b.nome_articolo, "it"));
  }, [products, form?.fornitore_id]);

  // Helpers for ordering by "confezione"
  const unitNameOf = (prodId) => {
    const p = products.find((x) => x.id === prodId);
    if (!p) return "";
    return units.find((u) => u.id === p.unita_misura_id)?.nome || "";
  };
  const pezziPerConf = (prodId) => {
    const p = products.find((x) => x.id === prodId);
    return Math.max(1, Number(p?.pezzi_per_confezione) || 1);
  };
  const isConfOrderable = (prodId) => {
    const p = products.find((x) => x.id === prodId);
    // A product is orderable in confezioni only when pezzi_per_confezione > 1 (1 = trivial / legacy default)
    return Number(p?.pezzi_per_confezione) > 1;
  };
  // Compute the stock of a product IN THE ORDER'S DESTINATION DEPOSIT (form.deposito_id).
  // Matches products by (nome_articolo case/space-insensitive + fornitore_id) so it works
  // even when the row's prodotto_id is a "template" record living in a different deposit.
  const stockInSelectedDeposit = (prodottoId) => {
    if (!form?.deposito_id) return null;
    const prod = products.find((p) => p.id === prodottoId);
    if (!prod) return null;
    const key = (prod.nome_articolo || "").trim().toLowerCase();
    return products
      .filter(
        (p) =>
          p.deposito_id === form.deposito_id &&
          p.fornitore_id === prod.fornitore_id &&
          (p.nome_articolo || "").trim().toLowerCase() === key
      )
      .reduce((s, p) => s + (Number(p.quantita) || 0), 0);
  };

  // Real (pezzi) quantity considering the chosen ordering unit ("pz" | "conf")
  const effectiveQty = (prodId, userQty, ordineUnit = "pz") => {
    const q = parseFloat(userQty) || 0;
    if (!q) return 0;
    return ordineUnit === "conf" ? q * pezziPerConf(prodId) : q;
  };

  // Build righe pre-populated with all supplier products,
  // merging quantities from existing order rows (used in both create and edit mode)
  const buildSupplierRows = (fid, existingRows = []) => {
    const supplierProducts = products.filter((p) => p.fornitore_id === fid);
    const groups = new Map();
    for (const p of supplierProducts) {
      const k = `${(p.nome_articolo || "").trim().toLowerCase()}::${p.fornitore_id || ""}`;
      if (!groups.has(k)) groups.set(k, { primary: p, totalQta: 0 });
      groups.get(k).totalQta += Number(p.quantita) || 0;
    }
    const existingById = new Map();
    const existingByName = new Map();
    existingRows.forEach((r) => {
      existingById.set(r.prodotto_id, r);
      const p = products.find((x) => x.id === r.prodotto_id);
      if (p) {
        const nameKey = (p.nome_articolo || "").trim().toLowerCase();
        existingByName.set(nameKey, r);
      }
    });
    return Array.from(groups.values())
      .map(({ primary, totalQta }) => {
        const nameKey = (primary.nome_articolo || "").trim().toLowerCase();
        const ex = existingById.get(primary.id) || existingByName.get(nameKey);
        const canConf = isAdmin && Number(primary.pezzi_per_confezione) > 1;
        return {
          prodotto_id: primary.id,
          quantita: ex?.quantita ?? "",
          ordine_unita: ex?.ordine_unita || (canConf ? "conf" : "pz"),
          prezzo_unitario: ex?.prezzo_unitario ?? (primary.prezzo_acquisto || ""),
          iva_percent: ex?.iva_percent ?? (primary.iva_acquisto || 22),
          _stock: totalQta,
          _nome: primary.nome_articolo,
        };
      })
      .sort((a, b) => (a._nome || "").localeCompare(b._nome || "", "it"));
  };

  const onFornitoreChange = (v) => {
    const fid = v === "none" ? "" : v;
    // pre-populate righe with all products of this supplier (only when in create mode)
    if (!editing && fid) {
      const items = buildSupplierRows(fid, []);
      setForm((f) => ({ ...f, fornitore_id: fid, righe: items.length ? items : [{ ...EMPTY_LINE }] }));
    } else {
      setForm({ ...form, fornitore_id: fid });
    }
  };

  const openCreate = () => {
    const pref = deposits.find((x) => x.preferito);
    setEditing(null);
    setForm({
      ...EMPTY_ORDER,
      deposito_id: pref?.id || deposits[0]?.id || "",
      righe: [{ ...EMPTY_LINE }],
    });
    setLineSearch("");
    setOpen(true);
  };

  const openEdit = (o) => {
    if (o.stato === "consegnato" || o.stato === "annullato") {
      toast.error(`Ordine ${o.stato} non modificabile`);
      return;
    }
    setEditing(o);
    // For draft/da_inviare: pre-populate ALL supplier products with quantities from existing order
    const existingRows = o.righe.map((r) => ({
      prodotto_id: r.prodotto_id,
      quantita: r.quantita,
      ordine_unita: r.ordine_unita || "pz",
      prezzo_unitario: r.prezzo_unitario,
      iva_percent: r.iva_percent,
    }));
    let righe;
    if (o.fornitore_id && o.stato === "bozza") {
      righe = buildSupplierRows(o.fornitore_id, existingRows);
      if (!righe.length) righe = existingRows;
    } else {
      righe = existingRows;
    }
    setForm({
      fornitore_id: o.fornitore_id || "",
      deposito_id: o.deposito_id,
      stato: o.stato,
      note: o.note || "",
      data_consegna_prevista: o.data_consegna_prevista || "",
      righe,
    });
    setLineSearch("");
    setOpen(true);
  };

  const setLine = (idx, key, value) => {
    setForm((f) => {
      const righe = [...f.righe];
      righe[idx] = { ...righe[idx], [key]: value };
      // auto-fill price/IVA when product is selected
      if (key === "prodotto_id") {
        const p = products.find((x) => x.id === value);
        if (p) {
          righe[idx].prezzo_unitario = p.prezzo_acquisto || 0;
          righe[idx].iva_percent = p.iva_acquisto || 22;
          // Default to 'conf' when admin AND product is orderable in confezioni (ppc > 1)
          const canConf = isAdmin && Number(p.pezzi_per_confezione) > 1;
          if (canConf && !righe[idx].ordine_unita) righe[idx].ordine_unita = "conf";
          if (canConf && righe[idx].ordine_unita === "pz") righe[idx].ordine_unita = "conf";
          if (!canConf) righe[idx].ordine_unita = "pz";
        }
      }
      return { ...f, righe };
    });
  };

  const addLine = () => setForm((f) => ({ ...f, righe: [...f.righe, { ...EMPTY_LINE }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, righe: f.righe.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.deposito_id) return toast.error("Seleziona un deposito");
    // Keep raw qty in the row's unit — backend computes quantita_base_calcolata
    const validRows = form.righe.filter((r) => r.prodotto_id && parseFloat(r.quantita) > 0);
    if (validRows.length === 0) {
      return toast.error("Inserisci la quantità per almeno un prodotto");
    }
    try {
      const payload = {
        ...form,
        fornitore_id: form.fornitore_id || null,
        data_consegna_prevista: form.data_consegna_prevista || null,
        righe: validRows.map((r) => {
          const wantsConf = r.ordine_unita === "conf" && isConfOrderable(r.prodotto_id);
          return {
            prodotto_id: r.prodotto_id,
            quantita: parseFloat(r.quantita) || 0,
            ordine_unita: wantsConf ? "conf" : "pz",
            prezzo_unitario: r.prezzo_unitario === "" ? 0 : parseFloat(r.prezzo_unitario) || 0,
            iva_percent: parseFloat(r.iva_percent) || 0,
          };
        }),
      };
      if (editing) {
        await api.put(`/orders/${editing.id}`, payload);
        toast.success("Ordine aggiornato");
      } else {
        await api.post("/orders", payload);
        toast.success("Ordine creato");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/orders/${deleteId}`);
      toast.success("Ordine eliminato");
      setDeleteId(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const doStatusChange = async () => {
    if (!statusChange) return;
    try {
      await api.post(`/orders/${statusChange.id}/status`, { stato: statusChange.stato });
      toast.success("Stato aggiornato");
      setStatusChange(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const doAutoGenerate = async () => {
    try {
      const { data } = await api.post("/orders/auto-generate");
      if (data.count === 0) {
        toast.info(
          data.skipped_no_supplier.length > 0
            ? `${data.total_low_stock} articoli sotto soglia, ma nessuno ha un fornitore associato.`
            : "Nessun articolo sotto soglia minima."
        );
      } else {
        toast.success(`Create ${data.count} bozze d'ordine`);
      }
      if (data.skipped_no_supplier?.length) {
        toast.warning(`${data.skipped_no_supplier.length} articoli senza fornitore ignorati`);
      }
      setAutoGenOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadPdf = async (order) => {
    try {
      const token = localStorage.getItem("ds_token");
      const res = await fetch(`${API}/orders/${order.id}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Errore generazione PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${order.numero || "ordine"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(err.message || "Errore download PDF");
    }
  };

  const sendEmail = async () => {
    if (!emailDialog) return;
    try {
      const { data } = await api.post(`/orders/${emailDialog.id}/send-email`, {
        recipient_email: emailRecipient || undefined,
      });
      toast.success(`Email inviata a ${data.recipient}`);
      setEmailDialog(null);
      setEmailRecipient("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const WHATSAPP_DEFAULT = "393280714855";

  const sendWhatsApp = async (o) => {
    try {
      // 1) Fetch order detail to compose a rich message
      const fornitore = suppliers.find((s) => s.id === o.fornitore_id);
      const deposito = deposits.find((d) => d.id === o.deposito_id);
      const productById = (id) => products.find((p) => p.id === id);
      const lines = (o.righe || []).map((r, i) => {
        const p = productById(r.prodotto_id);
        return `${i + 1}. ${p?.nome_articolo || "—"} x ${r.quantita}`;
      });
      const totalFmt = Number(o.totale || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const text = [
        `*Ordine ${o.numero || ""}* — Webo24`,
        fornitore ? `Fornitore: ${fornitore.ragione_sociale}` : null,
        deposito ? `Consegna a: ${deposito.nome}` : null,
        "",
        "Articoli richiesti:",
        ...lines,
        "",
        `Totale: € ${totalFmt}`,
        o.note ? `Note: ${o.note}` : null,
        "",
        "Buona giornata!",
      ].filter(Boolean).join("\n");

      // 2) Determine number: fornitore telefono > default
      const rawPhone = fornitore?.telefono || WHATSAPP_DEFAULT;
      const cleanPhone = rawPhone.replace(/[^\d]/g, "");
      const finalPhone = cleanPhone.startsWith("39") || cleanPhone.length >= 11 ? cleanPhone : `39${cleanPhone}`;

      const url = `https://wa.me/${finalPhone}?text=${encodeURIComponent(text)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.info("WhatsApp aperto in una nuova scheda. Ricordati di allegare il PDF se necessario.");
    } catch (err) {
      toast.error("Impossibile aprire WhatsApp");
    }
  };

  const filtered = useMemo(() => {
    let l = orders;
    if (filter !== "tutti") l = l.filter((o) => o.stato === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      l = l.filter((o) =>
        [o.numero, o.fornitore_nome, o.deposito_nome, o.note].some((v) => v && String(v).toLowerCase().includes(q))
      );
    }    return l;
  }, [orders, filter, search]);

  const { toggleSort, SortIcon, sortedRows: sortedFiltered } = useSort(filtered, {
    initial: { by: "data_ordine", dir: "desc" },
    getKey: (row, by) => {
      if (by === "righe") return row.righe?.length || 0;
      if (by === "totale") return Number(row.totale) || 0;
      return row[by];
    },
  });

  // Compute totals preview — prezzo_unitario is per row-unit (per confezione if 'conf'), so total = qty * price
  const formTotals = useMemo(() => {
    let imp = 0, iva = 0;
    form.righe.forEach((r) => {
      const q = parseFloat(r.quantita) || 0;
      const p = parseFloat(r.prezzo_unitario) || 0;
      const i = parseFloat(r.iva_percent) || 0;
      const sub = q * p;
      imp += sub;
      iva += (sub * i) / 100;
    });
    return { imponibile: imp, iva, totale: imp + iva };
  }, [form.righe]);

  return (
    <div className="space-y-6" data-testid="ordini-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="label-tag mb-2">Operazioni</div>
          <h1 className="font-brand text-4xl sm:text-5xl font-light tracking-tight">Ordini</h1>
          <p className="text-sm mt-2" style={{ color: "var(--ds-text-secondary)" }}>
            Crea ordini ai fornitori e gestiscili dalla bozza alla consegna.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          {canCreate && (
            <Button
              onClick={() => setAutoGenOpen(true)}
              variant="outline"
              className="rounded-lg"
              style={{ borderColor: "var(--ds-primary)", color: "var(--ds-primary)" }}
              data-testid="ordine-auto-generate-button"
            >
              <Sparkles size={16} className="mr-1.5" /> Riordino automatico
            </Button>
          )}
          {canCreate && (
            <Button onClick={openCreate} className="ds-btn-primary" data-testid="ordine-add-button">
              <Plus size={16} className="mr-1.5" /> Nuovo ordine
            </Button>
          )}
        </div>
      </div>

      <div className="ds-card p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <Tabs value={filter} onValueChange={changeFilter} className="w-full md:w-auto">
            <TabsList className="bg-white border" style={{ borderColor: "var(--ds-border)" }}>
              <TabsTrigger value="tutti" data-testid="filter-tutti">Tutti</TabsTrigger>
              <TabsTrigger value="bozza" data-testid="filter-bozza">Bozze</TabsTrigger>
              <TabsTrigger value="da_inviare" data-testid="filter-da-inviare">Da inviare</TabsTrigger>
              <TabsTrigger value="in_consegna" data-testid="filter-in-consegna">In consegna</TabsTrigger>
              <TabsTrigger value="consegnato" data-testid="filter-consegnato">Consegnati</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative md:ml-auto flex-1 md:max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ds-text-secondary)" }} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca ordine…" className="pl-9 rounded-lg" data-testid="ordini-search" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("numero")} data-testid="sort-ord-numero">Numero<SortIcon col="numero" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("stato")} data-testid="sort-ord-stato">Stato<SortIcon col="stato" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("fornitore_nome")} data-testid="sort-ord-fornitore">Fornitore<SortIcon col="fornitore_nome" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("deposito_nome")} data-testid="sort-ord-deposito">Deposito<SortIcon col="deposito_nome" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("righe")} data-testid="sort-ord-righe">Righe<SortIcon col="righe" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("totale")} data-testid="sort-ord-totale">Totale<SortIcon col="totale" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("data_ordine")} data-testid="sort-ord-data">Data<SortIcon col="data_ordine" /></TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm" style={{ color: "var(--ds-text-secondary)" }}>Caricamento…</TableCell></TableRow>}
              {!loading && sortedFiltered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm" style={{ color: "var(--ds-text-secondary)" }}>Nessun ordine</TableCell></TableRow>}
              {sortedFiltered.map((o) => (
                <TableRow key={o.id} data-testid={`ordine-row-${o.id}`}>
                  <TableCell className="font-mono font-medium text-sm">{o.numero}</TableCell>
                  <TableCell><StateBadge stato={o.stato} /></TableCell>
                  <TableCell className="text-sm">{o.fornitore_nome || "—"}</TableCell>
                  <TableCell className="text-sm">{o.deposito_nome || "—"}</TableCell>
                  <TableCell className="text-sm">{o.righe?.length || 0}</TableCell>
                  <TableCell className="font-medium text-sm">€ {Number(o.totale || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-xs" style={{ color: "var(--ds-text-secondary)" }}>
                    {new Date(o.created_at).toLocaleDateString("it-IT")}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" title="Scarica PDF" onClick={() => downloadPdf(o)} data-testid={`ordine-pdf-${o.id}`}>
                      <Download size={14} />
                    </Button>
                    {canSend && (
                      <Button size="sm" variant="ghost" title="Invia per email" onClick={() => { setEmailDialog(o); setEmailRecipient(""); }} data-testid={`ordine-email-${o.id}`}>
                        <Mail size={14} />
                      </Button>
                    )}
                    {canSend && (
                      <Button size="sm" variant="ghost" title="Invia su WhatsApp" onClick={() => sendWhatsApp(o)} data-testid={`ordine-whatsapp-${o.id}`}>
                        <MessageCircle size={14} style={{ color: "#25D366" }} />
                      </Button>
                    )}
                    {canEdit && o.stato === "bozza" && (
                      <Button size="sm" variant="ghost" title="Invia ordine" onClick={() => setStatusChange({ id: o.id, stato: "da_inviare", label: "Marca come 'Da inviare'" })} data-testid={`ordine-send-${o.id}`}>
                        <Send size={14} />
                      </Button>
                    )}
                    {canEdit && o.stato === "da_inviare" && (
                      <Button size="sm" variant="ghost" title="In consegna" onClick={() => setStatusChange({ id: o.id, stato: "in_consegna", label: "Marca come 'In consegna'" })} data-testid={`ordine-ship-${o.id}`}>
                        <Truck size={14} />
                      </Button>
                    )}
                    {canEdit && o.stato === "in_consegna" && (
                      <Button size="sm" variant="ghost" title="Consegnato" onClick={() => setStatusChange({ id: o.id, stato: "consegnato", label: "Conferma consegna (caricherà i prodotti in magazzino)" })} data-testid={`ordine-deliver-${o.id}`}>
                        <CheckCircle2 size={14} style={{ color: "#7B8B6F" }} />
                      </Button>
                    )}
                    {canEdit && o.stato === "bozza" && (
                      <Button size="sm" variant="ghost" onClick={() => openEdit(o)} data-testid={`ordine-edit-${o.id}`} title="Modifica">
                        <Pencil size={14} />
                      </Button>
                    )}
                    {canEdit && o.stato !== "consegnato" && o.stato !== "annullato" && (
                      <Button size="sm" variant="ghost" title="Annulla" onClick={() => setStatusChange({ id: o.id, stato: "annullato", label: "Annulla ordine" })}>
                        <XCircle size={14} style={{ color: "var(--ds-danger)" }} />
                      </Button>
                    )}
                    {canDelete && o.stato !== "consegnato" && (
                      <Button size="sm" variant="ghost" onClick={() => setDeleteId(o.id)} data-testid={`ordine-delete-${o.id}`}>
                        <Trash2 size={14} style={{ color: "var(--ds-danger)" }} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Dialog Form */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setLineSearch(""); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ordine-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-3xl font-light">
              {editing ? `Modifica ordine ${editing.numero}` : "Nuovo ordine"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-6 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="label-tag">Fornitore</Label>
                <Select value={form.fornitore_id || "none"} onValueChange={onFornitoreChange}>
                  <SelectTrigger className="mt-1.5 rounded-lg" data-testid="ordine-fornitore">
                    <SelectValue placeholder="Seleziona…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno</SelectItem>
                    {suppliersSorted.map((s) => <SelectItem key={s.id} value={s.id}>{s.ragione_sociale}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-tag">Deposito *</Label>
                <Select value={form.deposito_id} onValueChange={(v) => setForm({ ...form, deposito_id: v })}>
                  <SelectTrigger className="mt-1.5 rounded-lg" data-testid="ordine-deposito">
                    <SelectValue placeholder="Seleziona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {depositsSorted.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome} {d.preferito ? "★" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-tag">Stato</Label>
                <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                  <SelectTrigger className="mt-1.5 rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bozza">Bozza</SelectItem>
                    <SelectItem value="da_inviare">Da inviare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-tag">Data consegna prevista</Label>
                <Input type="date" value={form.data_consegna_prevista || ""} onChange={(e) => setForm({ ...form, data_consegna_prevista: e.target.value })} className="mt-1.5 rounded-lg" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="label-tag">Righe d'ordine</div>
                <div className="flex items-center gap-2 flex-1 max-w-md">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ds-text-secondary)" }} />
                    <Input
                      value={lineSearch}
                      onChange={(e) => setLineSearch(e.target.value)}
                      placeholder="Cerca prodotto…"
                      className="rounded-lg pl-9 h-9 text-sm"
                      data-testid="ordine-line-search"
                    />
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addLine} className="rounded-lg" data-testid="ordine-add-line">
                    <Plus size={14} className="mr-1" /> Aggiungi riga
                  </Button>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--ds-border)", background: "white" }}>
                <div className="grid grid-cols-[minmax(0,2.6fr)_64px_72px_minmax(0,1.3fr)_minmax(0,1fr)_64px_36px] gap-3 px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium" style={{ background: "var(--ds-bg)", color: "var(--ds-text-secondary)" }}>
                  <div>Prodotto</div>
                  <div className="text-center">U.M.</div>
                  <div className="text-right" title="Giacenza attuale nel deposito destinazione dell'ordine">
                    Scorta<br /><span className="text-[9px] normal-case font-normal opacity-70">deposito ordine</span>
                  </div>
                  <div>Quantità</div>
                  <div className="text-right">Prezzo unit.</div>
                  <div className="text-right">IVA</div>
                  <div></div>
                </div>
                <div className="max-h-[480px] overflow-y-auto divide-y" style={{ borderColor: "var(--ds-border)" }}>
                {(() => {
                  const q = (lineSearch || "").trim().toLowerCase();
                  const visibleRows = form.righe
                    .map((r, idx) => ({ r, idx }))
                    .filter(({ r }) => {
                      if (!q) return true;
                      const prod = products.find((p) => p.id === r.prodotto_id);
                      const name = ((r._nome || prod?.nome_articolo) || "").toLowerCase();
                      // Always show the row currently being edited (has a value)
                      if (parseFloat(r.quantita) > 0) return true;
                      return name.includes(q);
                    });
                  if (visibleRows.length === 0 && q) {
                    return (
                      <div className="px-4 py-6 text-center text-sm" style={{ color: "var(--ds-text-secondary)" }}>
                        Nessun prodotto trovato per "{lineSearch}"
                      </div>
                    );
                  }
                  return visibleRows.map(({ r, idx }) => {
                  const prod = products.find((p) => p.id === r.prodotto_id);
                  const stock = stockInSelectedDeposit(r.prodotto_id);
                  const um = unitNameOf(r.prodotto_id);
                  const ppc = pezziPerConf(r.prodotto_id);
                  const isConf = r.ordine_unita === "conf";
                  const qty = parseFloat(r.quantita) || 0;
                  const realQty = isConf ? qty * ppc : qty;
                  const rowTotal = qty * (parseFloat(r.prezzo_unitario) || 0);
                  const hasQty = qty > 0;
                  return (
                  <div
                    key={r.prodotto_id || `line-${idx}`}
                    className="grid grid-cols-[minmax(0,2.6fr)_64px_72px_minmax(0,1.3fr)_minmax(0,1fr)_64px_36px] gap-3 items-start px-4 py-3"
                    style={{ background: hasQty ? "rgba(180, 107, 82, 0.04)" : undefined }}
                  >
                    <div className="min-w-0">
                      <Select value={r.prodotto_id || ""} onValueChange={(v) => setLine(idx, "prodotto_id", v)}>
                        <SelectTrigger className="rounded-md h-9 text-sm" data-testid={`line-product-${idx}`}>
                          <SelectValue placeholder="Seleziona prodotto…" />
                        </SelectTrigger>
                        <SelectContent>
                          {productsForOrder.length === 0 && (
                            <div className="px-2 py-2 text-xs" style={{ color: "var(--ds-text-secondary)" }}>
                              {form.fornitore_id ? "Nessun prodotto associato a questo fornitore" : "Nessun prodotto"}
                            </div>
                          )}
                          {productsForOrder.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome_articolo}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {isAdmin && r.prodotto_id && isConfOrderable(r.prodotto_id) && (
                        <div className="inline-flex items-center gap-px mt-1.5 rounded-full p-0.5" style={{ background: "var(--ds-bg)" }}>
                          <button
                            type="button"
                            onClick={() => setLine(idx, "ordine_unita", "pz")}
                            className="px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors"
                            style={{
                              background: r.ordine_unita !== "conf" ? "var(--ds-primary)" : "transparent",
                              color: r.ordine_unita !== "conf" ? "white" : "var(--ds-text-secondary)",
                            }}
                            data-testid={`line-unit-pz-${idx}`}
                          >
                            Pezzi
                          </button>
                          <button
                            type="button"
                            onClick={() => setLine(idx, "ordine_unita", "conf")}
                            className="px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors"
                            style={{
                              background: isConf ? "var(--ds-primary)" : "transparent",
                              color: isConf ? "white" : "var(--ds-text-secondary)",
                            }}
                            data-testid={`line-unit-conf-${idx}`}
                          >
                            Confezioni ·{ppc}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="text-center text-xs pt-2 font-medium" data-testid={`line-um-${idx}`} style={{ color: "var(--ds-text-secondary)" }}>
                      {um || "—"}
                    </div>
                    <div className="text-right text-sm pt-2 font-medium" data-testid={`line-stock-${idx}`} style={{ color: stock === 0 ? "var(--ds-text-secondary)" : "var(--ds-text-primary)" }}>
                      {stock != null ? stock : "—"}
                    </div>
                    <div>
                      <Input
                        type="number"
                        step="any"
                        value={r.quantita === 0 || r.quantita == null ? "" : r.quantita}
                        placeholder="0"
                        onChange={(e) => setLine(idx, "quantita", e.target.value === "" ? "" : parseFloat(e.target.value))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const next = document.querySelector(`[data-testid="line-qty-${idx + 1}"]`);
                            if (next) next.focus();
                          }
                        }}
                        className="rounded-md h-9 text-sm font-medium"
                        data-testid={`line-qty-${idx}`}
                      />
                      {isConf && hasQty && (
                        <div className="text-[10px] mt-1 font-medium" style={{ color: "var(--ds-primary)" }}>
                          = {realQty} {um || "pz"}
                        </div>
                      )}
                      {hasQty && rowTotal > 0 && (
                        <div className="text-[10px] mt-0.5" style={{ color: "var(--ds-text-secondary)" }}>
                          Riga: € {rowTotal.toFixed(2)}
                        </div>
                      )}
                    </div>
                    <div>
                      <Input type="number" step="0.01" value={r.prezzo_unitario === 0 || r.prezzo_unitario == null ? "" : r.prezzo_unitario} placeholder="—"
                        onChange={(e) => setLine(idx, "prezzo_unitario", e.target.value === "" ? "" : parseFloat(e.target.value))}
                        className="rounded-md h-9 text-sm text-right" data-testid={`line-price-${idx}`} />
                    </div>
                    <div>
                      <Input type="number" step="0.01" value={r.iva_percent} onChange={(e) => setLine(idx, "iva_percent", parseFloat(e.target.value) || 0)} className="rounded-md h-9 text-sm text-right" />
                    </div>
                    <div className="flex justify-end pt-1">
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(idx)} disabled={form.righe.length === 1}>
                        <Trash2 size={14} style={{ color: "var(--ds-danger)" }} />
                      </Button>
                    </div>
                  </div>
                  );
                });
                })()}
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-6 text-sm">
                <div>
                  <div className="label-tag">Imponibile</div>
                  <div className="font-medium">€ {formTotals.imponibile.toFixed(2)}</div>
                </div>
                <div>
                  <div className="label-tag">IVA</div>
                  <div className="font-medium">€ {formTotals.iva.toFixed(2)}</div>
                </div>
                <div>
                  <div className="label-tag">Totale</div>
                  <div className="font-primary font-semibold text-lg" style={{ color: "var(--ds-primary)" }} data-testid="ordine-totale">
                    € {formTotals.totale.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label className="label-tag">Note</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5 rounded-lg" rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="ordine-submit">
                {editing ? "Salva modifiche" : "Crea ordine"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'ordine?</AlertDialogTitle>
            <AlertDialogDescription>Operazione irreversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} style={{ background: "var(--ds-danger)" }}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!statusChange} onOpenChange={(o) => !o && setStatusChange(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi il cambio di stato?</AlertDialogTitle>
            <AlertDialogDescription>
              {statusChange?.label}
              {statusChange?.stato === "consegnato" && (
                <span className="block mt-2 text-sm" style={{ color: "var(--ds-primary)" }}>
                  Le quantità degli articoli saranno automaticamente caricate in magazzino.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doStatusChange} style={{ background: "var(--ds-primary)" }} data-testid="ordine-confirm-status">
              Conferma
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Auto-generate confirm */}
      <AlertDialog open={autoGenOpen} onOpenChange={setAutoGenOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Sparkles size={18} style={{ color: "var(--ds-primary)" }} /> Riordino automatico
            </AlertDialogTitle>
            <AlertDialogDescription>
              Per ogni fornitore con prodotti sotto soglia, verrà creata una bozza d'ordine.
              La quantità richiesta è calcolata per riportare lo stock a <b>2× la soglia minima</b>,
              arrotondata al multiplo dei pezzi per confezione.
              <br /><br />
              I prodotti senza fornitore associato verranno ignorati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doAutoGenerate} style={{ background: "var(--ds-primary)" }} data-testid="ordine-confirm-auto-generate">
              Genera bozze
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email dialog */}
      <Dialog open={!!emailDialog} onOpenChange={(o) => !o && setEmailDialog(null)}>
        <DialogContent data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-2xl font-light flex items-center gap-2">
              <Mail size={18} /> Invia ordine {emailDialog?.numero}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
              Verrà inviata un'email con il PDF in allegato. Se l'ordine è in bozza/da inviare,
              passerà automaticamente in stato <b>"In consegna"</b>.
            </p>
            <div>
              <Label className="label-tag">Destinatario</Label>
              <Input
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder={
                  suppliers.find((s) => s.id === emailDialog?.fornitore_id)?.email
                  || "indirizzo@esempio.it"
                }
                className="mt-1.5 rounded-lg"
                data-testid="email-recipient-input"
              />
              <div className="text-xs mt-1" style={{ color: "var(--ds-text-secondary)" }}>
                Lascia vuoto per usare l'email del fornitore associato.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(null)}>Annulla</Button>
            <Button onClick={sendEmail} className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="email-send-button">
              <Send size={14} className="mr-1.5" /> Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
